import { useEffect, useState } from 'react';
import { Card, StatusBadge } from '@afrilink/ui';
import {
  adminDomainService,
  type TldPricingRow,
} from '@/domains/domains-registry/services/adminDomainService';
import { formatZAR } from '@/shared/utils/format';

/**
 * Retail vs wholesale per TLD, read from `system_setting.domains_pricing`.
 *
 * 2026-09-25 (domain gap analysis G11). `wholesale`, `wholesale_at` and
 * `margin_rule` have been stored per row since 2026-09-13 and nothing
 * displayed them — six TLDs had been sold below cost for ten weeks with the
 * numbers sitting in a JSON column. A margin the owner cannot see is not a
 * control. Renewal wholesale is shown where it is known (only .africa so
 * far) and flagged "unverified" where the table assumes renew = register,
 * which the registrar confirmed on 2026-09-14 is NOT a safe assumption for
 * gTLDs (prices float with USD/ZAR).
 */
export function TldMarginCard() {
  const [rows, setRows] = useState<TldPricingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminDomainService
      .listTldPricing()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) return null;
  if (!rows) return null;

  const oldest = rows.reduce<string | null>(
    (acc, r) => (r.wholesale_at && (!acc || r.wholesale_at < acc) ? r.wholesale_at : acc),
    null,
  );

  return (
    <Card interactive={false} className="p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Margin per TLD</h2>
        <span className="text-xs text-muted-foreground">
          Wholesale last verified {oldest ?? 'never'} · registrar prices float with USD/ZAR — re-verify quarterly
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-3">TLD</th>
              <th className="py-1 pr-3 text-right">Register</th>
              <th className="py-1 pr-3 text-right">Cost</th>
              <th className="py-1 pr-3 text-right">Margin</th>
              <th className="py-1 pr-3 text-right">Renew</th>
              <th className="py-1 pr-3 text-right">Renew cost</th>
              <th className="py-1 text-right">Renew margin</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const regMargin = r.wholesale == null ? null : r.register - r.wholesale;
              const renewCost = r.renew_wholesale ?? r.wholesale;
              const renewMargin = renewCost == null ? null : r.renew - renewCost;
              const renewVerified = r.renew_wholesale != null || r.tld.endsWith('.za') || r.tld === 'co.za';
              return (
                <tr key={r.tld} className="border-t border-border">
                  <td className="py-1.5 pr-3 font-medium text-foreground">.{r.tld}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{formatZAR(r.register)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {r.wholesale == null ? '—' : formatZAR(r.wholesale)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {regMargin == null ? '—' : (
                      <span className={regMargin <= 0 ? 'font-semibold text-danger' : 'text-foreground'}>
                        {formatZAR(regMargin)}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{formatZAR(r.renew)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {renewCost == null ? '—' : formatZAR(renewCost)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    <span className="inline-flex items-center justify-end gap-2">
                      {renewMargin == null ? '—' : (
                        <span className={renewMargin <= 0 ? 'font-semibold text-danger' : 'text-foreground'}>
                          {formatZAR(renewMargin)}
                        </span>
                      )}
                      {!renewVerified && <StatusBadge tone="expiring">unverified</StatusBadge>}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
